import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import KeyboardIcon from '@mui/icons-material/Keyboard'
import {
	Box,
	Divider,
	IconButton,
	Popover,
	Tooltip,
	Typography,
} from '@mui/material'
import { useState } from 'react'

import { useAppContext } from '@/hooks'

// Detect Mac OS
const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform)
const modKey = isMac ? '⌘' : 'Ctrl'
const altKey = isMac ? '⌥' : 'Alt'
const deleteKey = isMac ? '⌫' : 'Del'

interface ShortcutItem {
	descriptionKey: string
	keys: string
}

interface ShortcutSection {
	shortcuts: ShortcutItem[]
	titleKey: string
}

const getShortcutSections = (): ShortcutSection[] => [
	{
		titleKey: 'SHORTCUTS_SECTION_GENERAL',
		shortcuts: [
			{ keys: `${modKey} + S`, descriptionKey: 'SHORTCUTS_SAVE_CHANGES' },
			{ keys: `${modKey} + PgUp/PgDn`, descriptionKey: 'SHORTCUTS_SWITCH_TABS' },
			{ keys: 'F11', descriptionKey: 'SHORTCUTS_FULLSCREEN_EDITOR' },
			{ keys: 'Esc', descriptionKey: 'SHORTCUTS_CANCEL_CLOSE' },
		],
	},
	{
		titleKey: 'SHORTCUTS_SECTION_TABLE_NAVIGATION',
		shortcuts: [
			{ keys: '↑ ↓ ← →', descriptionKey: 'SHORTCUTS_NAVIGATE_CELLS' },
			{ keys: `${modKey} + ↑/↓`, descriptionKey: 'SHORTCUTS_JUMP_FIRST_LAST_ROW' },
			{ keys: 'F2 / Enter', descriptionKey: 'SHORTCUTS_EDIT_CELL' },
			{ keys: 'Tab', descriptionKey: 'SHORTCUTS_NEXT_CELL' },
		],
	},
	{
		titleKey: 'SHORTCUTS_SECTION_TABLE_EDITING',
		shortcuts: [
			{ keys: `${modKey} + Enter`, descriptionKey: 'SHORTCUTS_INSERT_ROW_BELOW' },
			{ keys: `${modKey} + ${deleteKey}`, descriptionKey: 'SHORTCUTS_DELETE_ROW' },
			{ keys: `${modKey} + Arrows`, descriptionKey: 'SHORTCUTS_SAVE_AND_MOVE_TO_CELL' },
		],
	},
	{
		titleKey: 'SHORTCUTS_SECTION_FOCUS_NAVIGATION',
		shortcuts: [
			{ keys: `${altKey} + ↑`, descriptionKey: 'SHORTCUTS_FOCUS_TOOLBAR' },
			{ keys: `${altKey} + ←`, descriptionKey: 'SHORTCUTS_FOCUS_BREADCRUMBS' },
			{ keys: `${modKey} + ↓`, descriptionKey: 'SHORTCUTS_JUMP_TO_EDITORS_BELOW' },
		],
	},
]

const ShortcutRow = ({ description, keys }: { description: string; keys: string }) => (
	<Box
		sx={{
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			py: 0.5,
			gap: 3,
		}}
	>
		<Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
			{description}
		</Typography>
		<Box sx={{ display: 'flex', gap: 0.5 }}>
			{keys.split(' + ').map((key, i) => (
				<Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
					{i > 0 && (
						<Typography variant="caption" color="text.disabled">
							+
						</Typography>
					)}
					<Box
						component="kbd"
						sx={{
							px: 0.75,
							py: 0.25,
							bgcolor: 'grey.100',
							border: '1px solid',
							borderColor: 'grey.300',
							borderRadius: 0.5,
							fontSize: 11,
							fontFamily: 'monospace',
							fontWeight: 500,
							color: 'text.primary',
							boxShadow: '0 1px 0 rgba(0,0,0,0.1)',
							minWidth: 20,
							textAlign: 'center',
						}}
					>
						{key}
					</Box>
				</Box>
			))}
		</Box>
	</Box>
)

export const KeyboardShortcutsHelp = () => {
	const { t } = useAppContext()
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
	const shortcutSections = getShortcutSections()

	const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget)
	}

	const handleClose = () => {
		setAnchorEl(null)
	}

	const open = Boolean(anchorEl)

	return (
		<>
			<Box sx={{ display: 'none' }}>
				<Tooltip title={t('KEYBOARD_SHORTCUTS')}>
					<IconButton
						onClick={handleOpen}
						size="small"
						sx={{
							color: 'text.secondary',
							'&:hover': { color: 'primary.main' },
						}}
					>
						<KeyboardIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</Box>

			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				slotProps={{
					paper: {
						sx: {
							p: 2,
							minWidth: 320,
							maxHeight: '80vh',
							overflow: 'auto',
						},
					},
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
					<HelpOutlineIcon color="primary" fontSize="small" />
					<Typography variant="subtitle1" fontWeight={600}>
						{t('KEYBOARD_SHORTCUTS')}
					</Typography>
				</Box>

				{shortcutSections.map((section, sectionIndex) => (
					<Box key={section.titleKey}>
						{sectionIndex > 0 && <Divider sx={{ my: 1.5 }} />}
						<Typography
							variant="overline"
							color="primary"
							sx={{ fontSize: 10, fontWeight: 600, letterSpacing: 1 }}
						>
							{t(section.titleKey)}
						</Typography>
						<Box sx={{ mt: 0.5 }}>
							{section.shortcuts.map((shortcut) => (
								<ShortcutRow
									key={shortcut.descriptionKey}
									keys={shortcut.keys}
									description={t(shortcut.descriptionKey)}
								/>
							))}
						</Box>
					</Box>
				))}

				<Divider sx={{ my: 1.5 }} />
				<Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
					{isMac ? t('SHORTCUTS_MAC_DETECTED') : t('SHORTCUTS_WIN_LINUX_DETECTED')} • {t('SHORTCUTS_PRESS_QUESTION_FOR_HELP')}
				</Typography>
			</Popover>
		</>
	)
}

